import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from '../../../hooks';
import { IMAGES } from '../../../assets';
import styles from './Navbar.module.css';

/**
 * Navbar Component
 * 
 * Main navigation component with responsive design and language toggle.
 * Follows best practices for maintainability and accessibility.
 * 
 * Features:
 * - Responsive design with mobile menu
 * - Language toggle functionality
 * - Smooth scrolling to sections
 * - Accessibility-compliant navigation
 * - CSS Modules for scoped styling
 * 
 * @component
 * @example
 * return (
 *   <Navbar />
 * )
 */
const Navbar = () => {
  const { t, language, toggleLanguage } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Add scroll listener for navbar background effect
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((open) => {
      const next = !open;
      // lock/unlock body scroll for mobile menu
      if (next) document.body.classList.add('modal-open');
      else document.body.classList.remove('modal-open');
      return next;
    });
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    document.body.classList.remove('modal-open');
  };

  const scrollToSection = (sectionId, retries = 5) => {
    const section = document.getElementById(sectionId);
    if (section) {
      if (sectionId === 'download') {
        // For download section, add offset and use retry logic
        requestAnimationFrame(() => {
          const yOffset = -80;
          const rect = section.getBoundingClientRect();
          const y = rect.top + window.pageYOffset + yOffset;
          
          // Check if the element seems properly positioned and has content
          if (rect.height > 50 && rect.width > 0 && rect.top >= 0) {
            window.scrollTo({ top: y, behavior: 'smooth' });
          } else if (retries > 0) {
            // Retry if the element isn't properly positioned yet
            setTimeout(() => scrollToSection(sectionId, retries - 1), 150);
          }
        });
      } else {
        // For other sections, use standard scrollIntoView
        section.scrollIntoView({ behavior: 'smooth' });
      }
    } else if (retries > 0) {
      // Element doesn't exist yet, retry
      setTimeout(() => scrollToSection(sectionId, retries - 1), 150);
    }
  };

  const handleNavigation = (sectionId, event) => {
    if (event) {
      event.preventDefault();
    }
    
    // Close mobile menu first to avoid any interference
    if (isMobileMenuOpen) {
      closeMobileMenu();
    }
    
    if (location.pathname !== '/') {
      navigate('/');
      // Wait longer for React components to mount and render
      setTimeout(() => {
        scrollToSection(sectionId);
      }, 500);
    } else {
      scrollToSection(sectionId);
    }
  };

  // Combine navbar classes
  const navbarClasses = `${styles.navbar} ${isScrolled ? styles.scrolled : ''}`;

  return (
    <nav 
      className={navbarClasses} 
      role="navigation" 
      aria-label={language === 'ar' ? 'شريط التنقل الرئيسي' : 'Primary Navigation'}
    >
      <div className={styles.container}>
        {/* Logo and Brand */}
        <Link to="/" className={styles.brand} aria-label={language === 'ar' ? 'الصفحة الرئيسية' : 'Home Page'}>
          <img 
            src={IMAGES.branding.logoMain} 
            alt={language === 'ar' ? 'شعار بوابة الليطاني' : 'Litani Gateway Logo'} 
            className={styles.logo}
          />
          <span className={styles.title}>
            {language === 'ar' ? 'بوابة الليطاني' : 'Litani Gateway'}
          </span>
        </Link>

        {/* Desktop Navigation */}
        <div className={styles.desktopNav}>
          <button
            onClick={(e) => handleNavigation('about', e)}
            className={styles.navLink}
            type="button"
            aria-label={language === 'ar' ? 'الانتقال إلى قسم حول التطبيق' : 'Navigate to About section'}
          >
            {t.navbar.about}
          </button>
          
          <Link 
            to="/privacy-policy" 
            className={styles.navLink}
            aria-label={language === 'ar' ? 'عرض سياسة الخصوصية' : 'View Privacy Policy'}
          >
            {language === 'ar' ? 'سياسة الخصوصية' : 'Privacy Policy'}
          </Link>
          
          <button
            onClick={(e) => handleNavigation('download', e)}
            className={`${styles.navLink} ${styles.downloadBtn}`}
            type="button"
            aria-label={language === 'ar' ? 'الانتقال إلى قسم التحميل' : 'Navigate to Download section'}
          >
            {t.navbar.download}
          </button>

          {/* Language Toggle */}
          <button
            onClick={toggleLanguage}
            className={styles.languageToggle}
            type="button"
            aria-label={language === 'ar' ? 'التبديل إلى الإنجليزية' : 'Switch to Arabic'}
          >
            {language === 'ar' ? 'English' : 'العربية'}
          </button>
        </div>

        {/* Mobile Menu Button */}
        <button
          className={styles.mobileMenuToggle}
          onClick={toggleMobileMenu}
          type="button"
          aria-expanded={isMobileMenuOpen}
          aria-controls="mobile-menu"
          aria-label={language === 'ar' ? 'فتح قائمة التنقل' : 'Toggle mobile menu'}
        >
          <span className={`${styles.hamburger} ${isMobileMenuOpen ? styles.active : ''}`}>
            <span></span>
            <span></span>
            <span></span>
          </span>
        </button>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <>
          <div 
            id="mobile-menu"
            className={styles.mobileMenu}
            role="dialog"
            aria-modal="true"
            aria-label={language === 'ar' ? 'قائمة التنقل المحمولة' : 'Mobile navigation menu'}
          >
            <div className={styles.mobileMenuContent}>
              <button
                onClick={(e) => handleNavigation('about', e)}
                className={styles.mobileNavLink}
                type="button"
                role="menuitem"
              >
                {t.navbar.about}
              </button>
              
              <Link 
                to="/privacy-policy" 
                className={styles.mobileNavLink}
                onClick={closeMobileMenu}
                role="menuitem"
              >
                {language === 'ar' ? 'سياسة الخصوصية' : 'Privacy Policy'}
              </Link>
              
              <button
                onClick={(e) => handleNavigation('download', e)}
                className={styles.mobileNavLink}
                type="button"
                role="menuitem"
              >
                {t.navbar.download}
              </button>

              {/* Mobile Language Toggle */}
              <button
                onClick={() => {
                  toggleLanguage();
                  closeMobileMenu();
                }}
                className={styles.mobileLanguageToggle}
                type="button"
                role="menuitem"
              >
                {language === 'ar' ? 'English' : 'العربية'}
              </button>
            </div>
          </div>

          {/* Mobile Menu Overlay */}
          <div 
            className={styles.mobileMenuOverlay} 
            onClick={closeMobileMenu}
            aria-hidden="true"
          />
        </>
      )}
    </nav>
  );
};

export default Navbar;